/**
 * @sazeo/sdk — Digital Signatures hooks
 *
 * Hooks for managing eSign envelopes, signature requests, and audit trails.
 * All requests carry the caller's Supabase JWT — RLS enforces tenant isolation.
 */

import { type TenantSupabaseClient, getTenantClient } from '@sazeo/supabase-tenant';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// ---------------------------------------------------------------------------
// Row types
// ---------------------------------------------------------------------------

export interface SignatureEnvelope {
  id: string;
  tenant_id: string;
  document_id: string | null;
  title: string;
  description: string | null;
  status: 'draft' | 'sent' | 'in_progress' | 'completed' | 'cancelled';
  signers_required: number;
  signers_completed: number;
  signing_order: 'sequential' | 'parallel';
  expires_at: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface SignatureRequest {
  id: string;
  envelope_id: string;
  signer_email: string;
  signer_name: string;
  signer_user_id: string | null;
  order_index: number;
  status: 'pending' | 'sent' | 'viewed' | 'signed' | 'declined';
  signed_at: string | null;
  signature_reference: string | null;
  ipv4_address_signed: string | null;
  ipv6_address_signed: string | null;
  user_agent_signed: string | null;
  created_at: string;
  updated_at: string;
}

export interface SignatureAuditLogEntry {
  id: string;
  envelope_id: string;
  action:
    | 'created'
    | 'sent'
    | 'viewed'
    | 'signed'
    | 'declined'
    | 'expired'
    | 'cancelled'
    | 'downloaded';
  actor_id: string | null;
  actor_email: string | null;
  timestamp: string;
  details: Record<string, unknown> | null;
}

// ---------------------------------------------------------------------------
// Helper: get Supabase client
// ---------------------------------------------------------------------------

function resolveClient(): TenantSupabaseClient {
  return getTenantClient();
}

// ---------------------------------------------------------------------------
// Hooks: Envelope CRUD
// ---------------------------------------------------------------------------

export function useCreateSignatureEnvelope() {
  const client = resolveClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      title: string;
      description?: string;
      document_id?: string;
      expires_at: string;
      signing_order?: 'sequential' | 'parallel';
    }) => {
      const { data, error } = await client
        .from('signature_envelopes')
        .insert({
          title: input.title,
          description: input.description || null,
          document_id: input.document_id || null,
          expires_at: input.expires_at,
          signing_order: input.signing_order || 'sequential',
          status: 'draft',
          signers_required: 0,
          signers_completed: 0,
        })
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data as SignatureEnvelope;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signature_envelopes'] });
    },
  });
}

export function useGetSignatureEnvelope(envelopeId: string) {
  const client = resolveClient();

  return useQuery({
    queryKey: ['signature_envelope', envelopeId],
    queryFn: async () => {
      const { data, error } = await client
        .from('signature_envelopes')
        .select()
        .eq('id', envelopeId)
        .single();

      if (error) throw new Error(error.message);
      return data as SignatureEnvelope;
    },
    enabled: !!envelopeId,
  });
}

export function useListSignatureEnvelopes() {
  const client = resolveClient();

  return useQuery({
    queryKey: ['signature_envelopes'],
    queryFn: async () => {
      const { data, error } = await client
        .from('signature_envelopes')
        .select()
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw new Error(error.message);
      return (data || []) as SignatureEnvelope[];
    },
  });
}

export function useUpdateSignatureEnvelope() {
  const client = resolveClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      envelopeId: string;
      data: Partial<Pick<SignatureEnvelope, 'title' | 'description'>>;
    }) => {
      const { data, error } = await client
        .from('signature_envelopes')
        .update(input.data)
        .eq('id', input.envelopeId)
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data as SignatureEnvelope;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['signature_envelope', variables.envelopeId] });
      queryClient.invalidateQueries({ queryKey: ['signature_envelopes'] });
    },
  });
}

export function useCancelSignatureEnvelope() {
  const client = resolveClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (envelopeId: string) => {
      // Defense-in-depth: verify the caller owns this envelope before mutating.
      // RLS enforces this too, but an explicit check ensures a 404 (not a cryptic
      // RLS error) when the id doesn't exist in the caller's tenant.
      const { data: existing } = await client
        .from('signature_envelopes')
        .select('id')
        .eq('id', envelopeId)
        .maybeSingle();
      if (!existing) throw new Error('Envelope not found');

      const { data, error } = await client
        .from('signature_envelopes')
        .update({ status: 'cancelled', updated_at: new Date().toISOString() })
        .eq('id', envelopeId)
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data as SignatureEnvelope;
    },
    onSuccess: (_, envelopeId) => {
      queryClient.invalidateQueries({ queryKey: ['signature_envelope', envelopeId] });
      queryClient.invalidateQueries({ queryKey: ['signature_envelopes'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Hooks: Add Signatories
// ---------------------------------------------------------------------------

export function useAddSignatory() {
  const client = resolveClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      envelopeId: string;
      signerEmail: string;
      signerName: string;
      orderIndex: number;
    }) => {
      const { data, error } = await client
        .from('signature_requests')
        .insert({
          envelope_id: input.envelopeId,
          signer_email: input.signerEmail,
          signer_name: input.signerName,
          order_index: input.orderIndex,
          status: 'pending',
        })
        .select()
        .single();

      if (error) throw new Error(error.message);

      // Update signers_required count on envelope
      const { data: envelope, error: envError } = await client
        .from('signature_envelopes')
        .select('signers_required')
        .eq('id', input.envelopeId)
        .single();

      if (!envError && envelope) {
        await client
          .from('signature_envelopes')
          .update({ signers_required: envelope.signers_required + 1 })
          .eq('id', input.envelopeId);
      }

      return data as SignatureRequest;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['signature_requests', variables.envelopeId] });
      queryClient.invalidateQueries({ queryKey: ['signature_envelope', variables.envelopeId] });
    },
  });
}

export function useRemoveSignatory() {
  const client = resolveClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (requestId: string) => {
      const { data: request, error: fetchError } = await client
        .from('signature_requests')
        .select('envelope_id')
        .eq('id', requestId)
        .single();

      if (fetchError) throw new Error(fetchError.message);

      const { error } = await client.from('signature_requests').delete().eq('id', requestId);

      if (error) throw new Error(error.message);

      // Update signers_required count
      if (request) {
        const { data: envelope } = await client
          .from('signature_envelopes')
          .select('signers_required')
          .eq('id', request.envelope_id)
          .single();

        if (envelope && envelope.signers_required > 0) {
          await client
            .from('signature_envelopes')
            .update({ signers_required: envelope.signers_required - 1 })
            .eq('id', request.envelope_id);
        }
      }

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signature_requests'] });
      queryClient.invalidateQueries({ queryKey: ['signature_envelopes'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Hooks: Send & Sign
// ---------------------------------------------------------------------------

export function useSendSignatureRequest() {
  const client = resolveClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (envelopeId: string) => {
      // Call Edge Function to send requests via Yousign
      const session = await client.auth.getSession();
      const token = session.data.session?.access_token;

      if (!token) throw new Error('Not authenticated');

      const response = await fetch('/functions/v1/documents-signature/send', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ envelope_id: envelopeId }),
      });

      if (!response.ok) {
        const err = (await response.json()) as { error?: { message?: string } };
        throw new Error(err.error?.message || 'Failed to send signature request');
      }

      return (await response.json()) as { status: string };
    },
    onSuccess: (_, envelopeId) => {
      queryClient.invalidateQueries({ queryKey: ['signature_envelope', envelopeId] });
      queryClient.invalidateQueries({ queryKey: ['signature_envelopes'] });
      queryClient.invalidateQueries({ queryKey: ['signature_requests', envelopeId] });
    },
  });
}

export function useSignDocument() {
  const client = resolveClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { requestId: string; signatureImageBase64?: string }) => {
      const session = await client.auth.getSession();
      const token = session.data.session?.access_token;

      if (!token) throw new Error('Not authenticated');

      const response = await fetch('/functions/v1/documents-signature/sign', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          request_id: input.requestId,
          signature_image: input.signatureImageBase64 || null,
        }),
      });

      if (!response.ok) {
        const err = (await response.json()) as { error?: { message?: string } };
        throw new Error(err.error?.message || 'Failed to sign document');
      }

      return (await response.json()) as { status: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signature_requests'] });
      queryClient.invalidateQueries({ queryKey: ['signature_envelopes'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Hooks: Status & Audit
// ---------------------------------------------------------------------------

export function useGetSignatureRequests(envelopeId: string) {
  const client = resolveClient();

  return useQuery({
    queryKey: ['signature_requests', envelopeId],
    queryFn: async () => {
      const { data, error } = await client
        .from('signature_requests')
        .select()
        .eq('envelope_id', envelopeId)
        .order('order_index', { ascending: true });

      if (error) throw new Error(error.message);
      return (data || []) as SignatureRequest[];
    },
    enabled: !!envelopeId,
  });
}

export function useGetSignatureAuditTrail(envelopeId: string) {
  const client = resolveClient();

  return useQuery({
    queryKey: ['signature_audit_trail', envelopeId],
    queryFn: async () => {
      const { data, error } = await client
        .from('signature_audit_log')
        .select()
        .eq('envelope_id', envelopeId)
        .order('timestamp', { ascending: false });

      if (error) throw new Error(error.message);
      return (data || []) as SignatureAuditLogEntry[];
    },
    enabled: !!envelopeId,
  });
}

export function useDownloadSignedDocument() {
  const client = resolveClient();

  return useMutation({
    mutationFn: async (envelopeId: string) => {
      const session = await client.auth.getSession();
      const token = session.data.session?.access_token;

      if (!token) throw new Error('Not authenticated');

      const response = await fetch(`/functions/v1/documents-signature/download/${envelopeId}`, {
        method: 'GET',
        headers: {
          authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const err = (await response.json()) as { error?: { message?: string } };
        throw new Error(err.error?.message || 'Failed to download document');
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'document.pdf';
      if (contentDisposition) {
        const match = /filename="([^"]+)"/.exec(contentDisposition);
        if (match?.[1]) filename = match[1];
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      return { success: true };
    },
  });
}
